import { getConfig } from '@qapline/config';
import i18n from '@qapline/translations/i18next';
import {
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query';
import { z } from 'zod';

import { clearCredentials, loginThunk } from '../auth';

const buildBaseQuery = () =>
    fetchBaseQuery({
        baseUrl: getConfig().api.url,
        credentials: 'include',
        prepareHeaders: async (headers: Headers, { getState }) => {
            const state = getState() as any;
            const token = state?.auth?.token as string | null | undefined;
            if (token) {
                headers.set('x-auth-token', token);
            }

            if (i18n.language) {
                headers.set('Accept-Language', i18n.language);
            }
            return headers;
        },
    });

export const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    Record<string, never>,
    FetchBaseQueryMeta
> = (args, api, extraOptions) => buildBaseQuery()(args, api, extraOptions);

export const baseQueryWithAuthRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
        // try to get a new token
        const refreshResult = await baseQuery(
            { url: getConfig().api.endpoints.auth.refresh, method: 'POST' },
            api,
            extraOptions,
        );

        if (refreshResult.data) {
            const refreshTokenData = z.object({ accessToken: z.string() }).safeParse(refreshResult.data);
            if (!refreshTokenData.success) {
                api.dispatch(clearCredentials());
                return result;
            }

            api.dispatch(loginThunk(refreshTokenData.data.accessToken));

            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(clearCredentials());
        }
    }

    return result;
};
